import React, { useState, useEffect } from 'react';
import { fetchSpeakers } from '../services/api';
import FeaturedSpeakers from '../components/FeaturedSpeakers';
import { useConferenceSettings } from '../contexts/ConferenceSettingsContext';

interface Speaker {
  id: string;
  name: string;
  title: string;
  image?: string;
  company?: string;
  bio?: string;
  bio_short?: string;
  talk_title?: string;
  talk_abstract?: string;
  linkedin_url?: string;
}

const Speakers: React.FC = () => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { settings } = useConferenceSettings();

  useEffect(() => {
    const loadSpeakers = async () => {
      try {
        setLoading(true);
        const speakersData = await fetchSpeakers();
        setSpeakers(speakersData.speakers);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load speakers');
        console.error('Error loading speakers:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSpeakers();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
        <div className="container mx-auto px-4 py-16">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
        <div className="container mx-auto px-4 py-16">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
      <div className="container mx-auto px-4 py-16">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
            Our Speakers
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Meet our distinguished speakers who will share their expertise and insights at the Language Intelligence Conference 2025.
          </p>
        </div>

        {/* Speakers Count */}
        {speakers.length > 0 && (
          <div className="text-center mb-8">
            <span className="inline-block bg-[#00529B] text-white px-4 py-2 rounded-full text-sm font-medium">
              {speakers.length} Speaker{speakers.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* All Speakers List */}
        {speakers.length > 0 ? (
          <FeaturedSpeakers 
            speakers={speakers} 
            showViewAllLink={false}
            showHeader={false}
          />
        ) : (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Speakers Available</h3>
            <p className="text-gray-600 mb-6">
              Our speaker lineup is being finalized. Check back soon for updates!
            </p>
          </div>
        )}

        {/* Call to Action - Only show when accepting CFP */}
        {settings?.accepting_cfp && (
          <div className="text-center mt-16 bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold mb-4 text-gray-800">
              Want to Speak at Our Conference?
            </h3>
            <p className="text-gray-600 mb-6">
              We're always looking for passionate experts to share their knowledge with our community.
            </p>
            <a
              href="/cfp"
              className="inline-flex items-center px-6 py-3 bg-[#00529B] text-white font-medium rounded-lg hover:bg-[#003D74] transition-colors duration-300"
            >
              Submit a Proposal
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Speakers;
